/**
 * OAuth 2.1 authorization-code + PKCE (S256) against MARA's authorization server (mara/app/api/oauth/routes.py).
 * Public client: token_endpoint_auth_methods 'none'. The access_token is an accounts API key that the Workspace API
 * accepts as a Bearer token. No refresh grant exists: on 401 or expiry, run the flow again.
 */

export interface OAuthMetadata {
  issuer: string;
  authorization_endpoint: string;
  token_endpoint: string;
  registration_endpoint: string;
  code_challenge_methods_supported?: string[];
}

export interface RegisteredClient { client_id: string; client_name: string; redirect_uris: string[] }

export interface TokenResponse { access_token: string; token_type: string; expires_in?: number }

export interface PendingAuth { verifier: string; state: string; clientId: string; redirectUri: string; tokenEndpoint: string; startedAt: number }

const PENDING_KEY = 'nanome-lite.oauth.pending';
const CLIENT_KEY = 'nanome-lite.oauth.client';
/** sessionStorage key of the stored token ({...TokenResponse, obtainedAt}); apps write it with storeToken(). */
export const TOKEN_KEY = 'nanome-lite.oauth.token';

export type StoredToken = TokenResponse & { obtainedAt: number };

/** Keep a token for storedToken() (the popup callback page stores it in the opener's session through this). */
export function storeToken(token: TokenResponse, session: Storage = sessionStorage): StoredToken {
  const stored: StoredToken = { ...token, obtainedAt: Date.now() };
  session.setItem(TOKEN_KEY, JSON.stringify(stored));
  return stored;
}

const b64url = (bytes: Uint8Array) => btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

export function randomUrlSafe(byteLength = 48): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return b64url(bytes);
}

export async function codeChallengeS256(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return b64url(new Uint8Array(digest));
}

export async function discover(maraUrl: string): Promise<OAuthMetadata> {
  const res = await fetch(`${maraUrl.replace(/\/$/, '')}/.well-known/oauth-authorization-server`);
  if (!res.ok) throw new Error(`OAuth discovery failed: ${res.status}`);
  return res.json();
}

/** Dynamic client registration (rate limit 10/min/IP). The client id is cached per (registration endpoint, redirect uri). */
export async function registerClient(meta: OAuthMetadata, clientName: string, redirectUri: string, storage: Storage = localStorage): Promise<RegisteredClient> {
  const cacheKey = `${CLIENT_KEY}:${meta.registration_endpoint}:${redirectUri}`;
  const cached = storage.getItem(cacheKey);
  if (cached) return JSON.parse(cached);
  const res = await fetch(meta.registration_endpoint, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ client_name: clientName, redirect_uris: [redirectUri] }),
  });
  if (!res.ok) throw new Error(`OAuth client registration failed: ${res.status} ${await res.text()}`);
  const client: RegisteredClient = await res.json();
  storage.setItem(cacheKey, JSON.stringify(client));
  return client;
}

export function forgetClient(meta: OAuthMetadata, redirectUri: string, storage: Storage = localStorage) {
  storage.removeItem(`${CLIENT_KEY}:${meta.registration_endpoint}:${redirectUri}`);
}

/** Builds the authorize URL and stores the pending verifier/state in sessionStorage. Caller navigates (redirect or popup). */
export async function beginAuthorization(meta: OAuthMetadata, client: RegisteredClient, redirectUri: string, session: Storage = sessionStorage): Promise<string> {
  const verifier = randomUrlSafe(48);
  const state = randomUrlSafe(16);
  const challenge = await codeChallengeS256(verifier);
  const pending: PendingAuth = { verifier, state, clientId: client.client_id, redirectUri, tokenEndpoint: meta.token_endpoint, startedAt: Date.now() };
  session.setItem(PENDING_KEY, JSON.stringify(pending));
  const u = new URL(meta.authorization_endpoint);
  u.searchParams.set('response_type', 'code');
  u.searchParams.set('client_id', client.client_id);
  u.searchParams.set('redirect_uri', redirectUri);
  u.searchParams.set('code_challenge', challenge);
  u.searchParams.set('code_challenge_method', 'S256');
  u.searchParams.set('state', state);
  return u.toString();
}

/** On the callback page: verifies state and exchanges the single-use code (60 s TTL) for a token. */
export async function completeAuthorization(callbackUrl: string, session: Storage = sessionStorage): Promise<TokenResponse> {
  const params = new URL(callbackUrl).searchParams;
  const code = params.get('code');
  const state = params.get('state');
  const error = params.get('error');
  if (error) throw new Error(`OAuth error: ${error} ${params.get('error_description') ?? ''}`);
  const raw = session.getItem(PENDING_KEY);
  if (!raw) throw new Error('No pending OAuth flow in this session');
  const pending: PendingAuth = JSON.parse(raw);
  if (!code || state !== pending.state) throw new Error('OAuth state mismatch');
  session.removeItem(PENDING_KEY);
  const body = new URLSearchParams({ grant_type: 'authorization_code', code, code_verifier: pending.verifier, client_id: pending.clientId, redirect_uri: pending.redirectUri });
  const res = await fetch(pending.tokenEndpoint, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body });
  if (!res.ok) throw new Error(`Token exchange failed: ${res.status} ${await res.text()}`);
  const token: TokenResponse = await res.json();
  storeToken(token, session);
  return token;
}

export function storedToken(session: Storage = sessionStorage): StoredToken | null {
  const raw = session.getItem(TOKEN_KEY);
  if (!raw) return null;
  const t = JSON.parse(raw);
  if (t.expires_in && Date.now() > t.obtainedAt + t.expires_in * 1000) { session.removeItem(TOKEN_KEY); return null; }
  return t;
}

export function clearToken(session: Storage = sessionStorage) { session.removeItem(TOKEN_KEY); }

export function isCallback(url: string): boolean {
  const p = new URL(url).searchParams;
  return p.has('code') || p.has('error');
}
